import clsx from 'clsx';
import { useFitText } from '@flyyer/use-fit-text';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import '../../../../font.css';

interface FitPriceOldProps {
  text?: string;
  width?: number;
  height?: number;
  matrix?: number[];
  top?: number;
  left?: number;
  right?: number;
  color?: [number, number, number];
  textAlign?: CSSProperties['textAlign'];
  display?: CSSProperties['display'];
  margin?: CSSProperties['margin'];
  isOld?: boolean;
  fontFamily?: CSSProperties['fontFamily'];
  fontWeight?: CSSProperties['fontWeight'];
  borderRadius?: CSSProperties['borderRadius'];
}

export default function FitPriceOld({
  width = 0,
  height = 0,
  matrix = [0, 0, 0, 0, 0, 0],
  text = '',
  top = 0,
  left = 0,
  right,
  color = [0, 0, 0],
  textAlign,
  display = 'flex',
  margin = 'auto',
  fontFamily = '"Primary"',
  fontWeight = 'normal',
}: FitPriceOldProps) {
  const [refresh, setRefresh] = useState(false);
  const isRight = right !== undefined ? true : false;
  // UI-dependent variables (title and description) are added to the hook's dependency array.
  const { ref, fontSize, isCalculating } = useFitText(
    {
      /** Depends on body's fontSize (usually 16px) */
      maxFontSize: 1000, // 1000%
      /** Lower values are more strict but causes more renders, defaults to `5` */
      resolution: 10,
    },
    [width, height, text, refresh],
  );

  function addPontis(str: string) {
    const reversedStr = str.split('').reverse().join(''); // Inverte a string
    const regex = /(\d{3})(?=\d)/g; // Regex para encontrar grupos de três dígitos
    const reversedFormattedStr = reversedStr.replace(regex, '$1.'); // Adiciona um ponto entre grupos de três dígitos
    const formattedStr = reversedFormattedStr.split('').reverse().join(''); // Inverte novamente para obter a string final
    return formattedStr;
  }
  const real = addPontis(Math.floor(+text / 100).toString());
  const cent = (+text % 100).toString().padStart(2, '0');

  const className = clsx({ 'flyyer-wait': isCalculating }); // use class flyyer-wait to prevent eager renders of flyyer templates.
  const rectangleRef = useRef<HTMLLabelElement>(null);
  const diagonalLineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const rectangle = rectangleRef.current;
    const diagonalLine = diagonalLineRef.current;
    if (rectangle && diagonalLine) {
      // Calcula o ângulo com base nas dimensões do retângulo
      const width = rectangle.offsetWidth;
      const height = rectangle.offsetHeight;
      const angle = Math.atan(height / width) * (180 / Math.PI); // Converte de radianos para graus

      // Aplica a rotação à linha diagonal
      diagonalLine.style.transform = `rotate(-${angle}deg)`;
    }
  });

  useEffect(() => {
    const timer = setInterval(() => {
      clearInterval(timer);
      setRefresh(!refresh);
    }, 1000);
  }, []);

  return (
    <div
      style={{
        transition: 'transform 500ms, color 500ms',
        transformOrigin: `${isRight ? 'right' : 'left'} top`,
        transform: `matrix(${matrix.join(', ')})`,
        color: `rgb(${color[0]},${color[1]},${color[2]})`,
      }}
    >
      <div
        ref={ref}
        style={{
          position: 'absolute',
          display,
          transition:
            'height 500ms, width 500ms, top 500ms, left 500ms, right 500ms',
          top,
          ...(() => (isRight ? { right } : { left }))(),
          fontSize,
          fontFamily,
          fontWeight,
          letterSpacing: '2px',
          height,
          width,
          whiteSpace: 'pre',
          textAlign,
        }}
        className={className}
      >
        <div
          style={{
            display: 'flex',
            position: 'relative',
            margin,
          }}
        >
          <span
            ref={rectangleRef}
            style={{
              fontSize,
              margin: 'auto 0',
            }}
          >
            {`${real},${cent}`}
            <div
              ref={diagonalLineRef}
              style={{
                position: 'absolute',
                background: 'black',
                minWidth: '100%',
                minHeight: '3px',
                transformOrigin: '50% 50%',
                top: '50%',
              }}
            ></div>
          </span>
        </div>
      </div>
    </div>
  );
}
